import { PropsWithChildren, useEffect } from 'react';
import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';

/**
 * Howden digital brand palette (Digital brand guidelines, August 2024, p8-9).
 * Only these colours are approved. Navy and blue-grey are not Howden colours.
 */
export const howden = {
  cobaltBlue: '#0857c3',
  pistachio: '#e0ec89',
  teal: '#7ce0d3',
  mossGreen: '#173f35',
  gold: '#8d741e',
  rosewood: '#3c0f11',
  mustard: '#ffbf3f',
  poppy: '#f9423a',

  grey: '#3d3d3d',
  grey60: '#8b8b8b',
  grey10: '#ececec',
  grey5: '#f5f5f5',
  white: '#ffffff',
  black: '#000000',
};

/**
 * One of the colour themes from the guidelines (p10). Each is built from a
 * "connecting" pair (p8). Text colours were chosen by measured WCAG contrast:
 * every text pairing is 4.5:1 or better, and the nav indicator is 3:1 or
 * better against the bar.
 */
export type HowdenThemeSpec = {
  /** Backstage theme id. `howden` stays the default (cobalt). */
  id: string;
  title: string;
  /** Links, buttons and headings on white. */
  primary: string;
  onPrimary: string;
  accent: string;
  onAccent: string;
  navBg: string;
  navText: string;
  navIndicator: string;
  navHover: string;
  heroBg: string;
  heroText: string;
  ctaBg: string;
  ctaText: string;
  /** A light tint of the primary, for highlighted rows and columns. */
  tint: string;
};

const DARK_HOVER = 'rgba(255, 255, 255, 0.12)';
const LIGHT_HOVER = 'rgba(0, 0, 0, 0.06)';

export const HOWDEN_THEMES: HowdenThemeSpec[] = [
  {
    // White on cobalt 6.6:1, black on teal 13.5:1 (cobalt on teal is 4.3:1).
    id: 'howden',
    title: 'Cobalt blue',
    primary: howden.cobaltBlue,
    onPrimary: howden.white,
    accent: howden.teal,
    onAccent: howden.black,
    navBg: howden.cobaltBlue,
    navText: howden.white,
    navIndicator: howden.teal,
    navHover: DARK_HOVER,
    heroBg: howden.cobaltBlue,
    heroText: howden.white,
    ctaBg: howden.teal,
    ctaText: howden.black,
    tint: '#f0f5fc',
  },
  {
    // White on moss 11.7:1, moss on pistachio 9.2:1.
    id: 'howden-moss',
    title: 'Moss green',
    primary: howden.mossGreen,
    onPrimary: howden.white,
    accent: howden.pistachio,
    onAccent: howden.mossGreen,
    navBg: howden.mossGreen,
    navText: howden.white,
    navIndicator: howden.pistachio,
    navHover: DARK_HOVER,
    heroBg: howden.mossGreen,
    heroText: howden.white,
    ctaBg: howden.pistachio,
    ctaText: howden.mossGreen,
    tint: '#eef3f1',
  },
  {
    // White on gold 4.5:1, black on mustard 12.8:1. Mustard on gold is only
    // 2.75:1, so the nav indicator is white.
    id: 'howden-gold',
    title: 'Gold',
    primary: howden.gold,
    onPrimary: howden.white,
    accent: howden.mustard,
    onAccent: howden.black,
    navBg: howden.gold,
    navText: howden.white,
    navIndicator: howden.white,
    navHover: DARK_HOVER,
    heroBg: howden.gold,
    heroText: howden.white,
    ctaBg: howden.mustard,
    ctaText: howden.black,
    tint: '#f8f4e8',
  },
  {
    // White on rosewood 16.6:1, black on poppy 5.9:1. The guidelines ask for
    // rosewood to be used sparingly (p10).
    id: 'howden-rosewood',
    title: 'Rosewood',
    primary: howden.rosewood,
    onPrimary: howden.white,
    accent: howden.poppy,
    onAccent: howden.black,
    navBg: howden.rosewood,
    navText: howden.white,
    navIndicator: howden.poppy,
    navHover: DARK_HOVER,
    heroBg: howden.rosewood,
    heroText: howden.white,
    ctaBg: howden.poppy,
    ctaText: howden.black,
    tint: '#f8eeee',
  },
  {
    // Grey on white 10.9:1, gold indicator on white 4.5:1, white on gold 4.5:1.
    id: 'howden-white',
    title: 'White',
    primary: howden.grey,
    onPrimary: howden.white,
    accent: howden.gold,
    onAccent: howden.white,
    navBg: howden.white,
    navText: howden.grey,
    navIndicator: howden.gold,
    navHover: LIGHT_HOVER,
    heroBg: howden.grey5,
    heroText: howden.grey,
    ctaBg: howden.grey,
    ctaText: howden.white,
    tint: howden.grey5,
  },
  {
    // Moss on pistachio 9.2:1, white on moss 11.7:1.
    id: 'howden-pistachio',
    title: 'Pistachio',
    primary: howden.mossGreen,
    onPrimary: howden.white,
    accent: howden.pistachio,
    onAccent: howden.mossGreen,
    navBg: howden.pistachio,
    navText: howden.mossGreen,
    navIndicator: howden.mossGreen,
    navHover: LIGHT_HOVER,
    heroBg: howden.pistachio,
    heroText: howden.mossGreen,
    ctaBg: howden.mossGreen,
    ctaText: howden.white,
    tint: '#f7fae3',
  },
];

export const DEFAULT_THEME = HOWDEN_THEMES[0];

/** CSS custom property names, set on <html> by the theme provider. */
const VARS: Record<keyof Omit<HowdenThemeSpec, 'id' | 'title'>, string> = {
  primary: '--brand-primary',
  onPrimary: '--brand-on-primary',
  accent: '--brand-accent',
  onAccent: '--brand-on-accent',
  navBg: '--brand-nav-bg',
  navText: '--brand-nav-text',
  navIndicator: '--brand-nav-indicator',
  navHover: '--brand-nav-hover',
  heroBg: '--brand-hero-bg',
  heroText: '--brand-hero-text',
  ctaBg: '--brand-cta-bg',
  ctaText: '--brand-cta-text',
  tint: '--brand-tint',
};

const cssVar = (key: keyof typeof VARS) =>
  `var(${VARS[key]}, ${DEFAULT_THEME[key]})`;

/**
 * Brand tokens for component styles. The theme-dependent ones are CSS
 * variables, so a theme change repaints every page without rebuilding styles.
 * They work anywhere CSS accepts a colour, but not in MUI colour maths
 * (alpha, darken), which need the real values from HOWDEN_THEMES.
 */
export const brand = {
  primary: cssVar('primary'),
  onPrimary: cssVar('onPrimary'),
  accent: cssVar('accent'),
  onAccent: cssVar('onAccent'),
  navBg: cssVar('navBg'),
  navText: cssVar('navText'),
  navIndicator: cssVar('navIndicator'),
  navHover: cssVar('navHover'),
  heroBg: cssVar('heroBg'),
  heroText: cssVar('heroText'),
  ctaBg: cssVar('ctaBg'),
  ctaText: cssVar('ctaText'),
  tint: cssVar('tint'),
  ink: howden.grey,
  muted: '#5c5c5c', // grey at 80%, 6.9:1 on white (60% tint fails AA for text)
  rule: howden.gold,
  focus: cssVar('primary'),
  surface: howden.white,
  subtle: howden.grey5,
  border: howden.grey10,
};

const headingFontFamily = '"GT Ultra Median", Georgia, serif';
const bodyFontFamily = '"Aktiv Grotesk", Arial, sans-serif';

/** The MUI and Backstage theme for one colour theme. */
export const createHowdenTheme = (spec: HowdenThemeSpec) => {
  const page = (shape: string) =>
    genPageTheme({ colors: [spec.primary, spec.accent], shape });
  return createUnifiedTheme({
    ...createBaseThemeOptions({
      palette: {
        ...palettes.light,
        primary: {
          main: spec.primary,
          light: spec.accent,
          dark: spec.primary,
          contrastText: spec.onPrimary,
        },
        secondary: {
          main: spec.primary,
          light: spec.accent,
          dark: spec.primary,
          contrastText: spec.onPrimary,
        },
        error: { main: howden.poppy },
        warning: { main: howden.mustard },
        info: { main: spec.primary },
        success: { main: spec.primary },
        background: { default: howden.white, paper: howden.grey5 },
        text: { primary: howden.grey, secondary: brand.muted },
        navigation: {
          background: spec.navBg,
          indicator: spec.navIndicator,
          color: spec.navText,
          selectedColor: spec.navText,
        },
      },
    }),

    fontFamily: bodyFontFamily,
    defaultPageTheme: 'home',
    pageTheme: {
      home: page(shapes.wave),
      documentation: page(shapes.wave2),
      tool: page(shapes.round),
      service: page(shapes.wave),
      website: page(shapes.wave),
      library: page(shapes.wave),
      other: page(shapes.wave),
      app: page(shapes.round),
      apis: page(shapes.wave),
    },

    typography: {
      htmlFontSize: 16,
      fontFamily: bodyFontFamily,
      h1: {
        fontFamily: headingFontFamily,
        fontSize: 60,
        fontWeight: 500,
        marginBottom: 0,
      },
      h2: {
        fontFamily: headingFontFamily,
        fontSize: 44,
        fontWeight: 500,
        marginBottom: 0,
      },
      h3: {
        fontFamily: headingFontFamily,
        fontSize: 32,
        fontWeight: 500,
        marginBottom: 0,
      },
      h4: {
        fontFamily: headingFontFamily,
        fontSize: 24,
        fontWeight: 500,
        marginBottom: 0,
      },
      h5: {
        fontFamily: headingFontFamily,
        fontSize: 20,
        fontWeight: 500,
        marginBottom: 0,
      },
      h6: {
        fontFamily: bodyFontFamily,
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 0,
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: howden.white,
            color: howden.grey,
            fontFamily: bodyFontFamily,
          },
          'h1, h2, h3, h4, h5': { fontFamily: headingFontFamily },
          a: { color: spec.primary, textDecorationColor: howden.gold },
          'a:hover': { color: spec.primary, textDecorationColor: spec.primary },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            minHeight: 40,
            padding: '8px 16px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
          },
          contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          containedPrimary: {
            backgroundColor: spec.primary,
            color: spec.onPrimary,
            '&:hover': { backgroundColor: spec.accent, color: spec.onAccent },
          },
          containedSecondary: {
            backgroundColor: spec.accent,
            color: spec.onAccent,
            '&:hover': { backgroundColor: spec.primary, color: spec.onPrimary },
          },
          outlinedPrimary: {
            borderColor: spec.primary,
            color: spec.primary,
            '&:hover': {
              borderColor: spec.primary,
              backgroundColor: spec.accent,
              color: spec.onAccent,
            },
          },
          textPrimary: {
            color: spec.primary,
            '&:hover': { backgroundColor: howden.grey5 },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 2 },
          elevation1: {
            boxShadow: 'none',
            border: `1px solid ${howden.grey10}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 2,
            border: `1px solid ${howden.grey10}`,
            boxShadow: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 2, fontFamily: bodyFontFamily },
          colorPrimary: { backgroundColor: spec.accent, color: spec.onAccent },
          colorSecondary: {
            backgroundColor: howden.grey5,
            color: spec.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            color: howden.grey,
            backgroundColor: howden.grey5,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          body1: { fontFamily: bodyFontFamily, fontSize: 16, lineHeight: 1.5 },
          body2: { fontFamily: bodyFontFamily, fontSize: 14, lineHeight: 1.5 },
        },
      },
    },
  });
};

/** The default (cobalt) theme, kept for existing imports. */
export const howdenLightTheme = createHowdenTheme(DEFAULT_THEME);

const builtThemes = new Map<string, ReturnType<typeof createHowdenTheme>>();
const themeFor = (spec: HowdenThemeSpec) => {
  if (!builtThemes.has(spec.id))
    builtThemes.set(spec.id, createHowdenTheme(spec));
  return builtThemes.get(spec.id)!;
};

/**
 * Applies a Howden colour theme: the MUI theme, a body class the BUI CSS
 * targets, and the brand CSS variables that component styles read.
 */
export function HowdenThemeProvider({
  children,
  spec = DEFAULT_THEME,
}: PropsWithChildren<{ spec?: HowdenThemeSpec }>) {
  useEffect(() => {
    const root = document.documentElement;
    document.body.classList.add('theme-howden');
    for (const [key, name] of Object.entries(VARS)) {
      root.style.setProperty(name, spec[key as keyof typeof VARS]);
    }
    return () => {
      document.body.classList.remove('theme-howden');
    };
  }, [spec]);

  return (
    <UnifiedThemeProvider theme={themeFor(spec)}>
      {children}
    </UnifiedThemeProvider>
  );
}
